import React, { useCallback, useMemo, useState } from 'react';
import { AppToolbox } from '../app/appToolbox';
import { AppButtonContained } from '../app/button/appButtonContained';
import { Box } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { AppControlledTextField } from '../app/input/appControlledTextField';
import { AppControlledAutocomplete } from '../app/input/appControlledAutocomplete';
import { toolboxStyles } from '../../styles/constants';
import { useDebounceFn } from '../../hooks/useDebounce';
import { useGetSalesPersonsQuery } from '../../store/employees/employeesService';
import { getOptions } from '../../services/employeeService';
import { AutocompleteOption } from '../../models/autocompleteOption';
import { DEFAULT_PAGE_SIZE, NO_RESULTS, PLEASE_TYPE } from '../../constants';
import { GetClientParams } from '../../services/api/clientServices';

enum SearchField {
  FILTER = 'filter',
  SALES_PERSON = 'salesPersonId',
}

interface ClientsToolboxFormValues {
  filter: string;
  salesPersonId: AutocompleteOption | null;
}

interface Props {
  onSearch(params: GetClientParams): void;
  onOpenAddDialog(): void;
}

export function ToolboxClients({
  onSearch,
  onOpenAddDialog,
}: Props): JSX.Element {
  const debounce = useDebounceFn(250);
  const methods = useForm<ClientsToolboxFormValues>();
  
  const [searchOptions, setSearchOptions] = useState<{
    value: string;
    field?: SearchField;
  }>({ value: '' });

  const { data: salesPersonsQueryData, isFetching: isFetchingSalesPersons } =
    useGetSalesPersonsQuery({
      take: 10,
      includeInactive: true,
      includeDeleted: true,
      ...{
        filter:
          searchOptions.field === SearchField.SALES_PERSON
            ? searchOptions.value
            : '',
      },
    });

  const salesPersonsOptions = useMemo(() => {
    return salesPersonsQueryData
      ? getOptions(salesPersonsQueryData?.items)
      : [];
  }, [salesPersonsQueryData]);

  const { watch } = methods;
  const watchedSalesPerson = watch(SearchField.SALES_PERSON);

  const onClientsSearch = useCallback(
    (e) => {
      debounce(() => {
        const filter = e.target.value;
        const params: GetClientParams = {};
        
        if (filter) {
          params.filter = filter;
        }
        
        const salesPersonId = (watchedSalesPerson as AutocompleteOption)?.id;
        if (salesPersonId) {
          params.salesPersonId = salesPersonId;
        }
        
        onSearch(params);
      });
    },
    [onSearch, debounce, watchedSalesPerson]
  );

  const searchSalesPersonOptions = useCallback((name: string) => {
    setSearchOptions({ value: name, field: SearchField.SALES_PERSON });
  }, []);

  const onResetSearch = useCallback(() => {
    setSearchOptions({ value: '' });
  }, []);

  const searchClientsBySalesPerson = useCallback(
    (data) => {
      const params: GetClientParams = {};
      
      const filter = methods.getValues(SearchField.FILTER);
      if (filter) {
        params.filter = filter;
      }
      
      const salesPersonId = data?.id;
      if (salesPersonId) {
        params.salesPersonId = salesPersonId;
      }
      
      onSearch(params);
      onResetSearch();
    },
    [onSearch, methods, onResetSearch]
  );

  return (
    <AppToolbox
      pageTitle="Clients"
      secondChildren={
        <AppButtonContained
          label="Client"
          handleClick={onOpenAddDialog}
          isIconButton
        />
      }
      thirdChildren={
        <Box sx={{ mb: 2 }}>
          <FormProvider {...methods}>
            <Box sx={toolboxStyles.inputsBox}>
              <AppControlledTextField
                name={SearchField.FILTER}
                label={'Client'}
                sx={toolboxStyles.input}
                onChangeFn={onClientsSearch}
              />
              <AppControlledAutocomplete
                name={SearchField.SALES_PERSON}
                label={'Sales'}
                searchOptions={searchSalesPersonOptions}
                options={salesPersonsOptions}
                sx={toolboxStyles.input}
                onSelect={searchClientsBySalesPerson}
                placeholder={PLEASE_TYPE}
                isLoading={isFetchingSalesPersons}
                noOptionsText={NO_RESULTS}
              />
            </Box>
          </FormProvider>
        </Box>
      }
    />
  );
}
